import { useState } from 'react';
import { RxCross2 } from 'react-icons/rx';
import { FileInput, Spinner, Tooltip } from 'flowbite-react';
import { useUserContext } from 'src/context/UserProvider';
import { useToast } from 'src/context/ToastProvider';
import { useMutation, useQueryClient } from 'react-query';
import { checkResumeContent, getFileNameFromeResumeLink } from '../../utils/resume';
import { getPolicyForS3NonVideo, updateUserById } from 'src/apis/user';
import { handleS3Upload } from 'src/utils/imageUpload';
import { get } from 'lodash';
import classNames from 'classnames';

interface Props {
	resumeLink: string;
}

const ProfileViewResume = ({ resumeLink }: Props) => {
	const { user } = useUserContext();
	const toast = useToast();
	const queryClient = useQueryClient();

	const [isResumeRemoveLoading, setIsResumeRemoveLoading] = useState<boolean>(false);
	const [isResumeUploadedLoading, setIsResumeUploadedLoading] = useState<boolean>(false);

	const updateProfileByIdFn = useMutation(['updateUserById'], updateUserById, {
		onSuccess() {
			queryClient.refetchQueries(['verifyCookie']);
			queryClient.refetchQueries(['getUserProfile']);
			toast({
				title: 'Action was successfully completed',
				type: 'success',
			});
			setIsResumeUploadedLoading(false);
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				type: 'error',
				title: errorMessage,
			});

			setIsResumeUploadedLoading(false);
		},
	});

	const handleUploadResume = async (resume: File) => {
		const fileSizeMB = resume.size / (1024 * 1024); // Convert bytes to MB
		if (fileSizeMB > 5) {
			toast({
				type: 'error',
				title: 'File size exceeds the maximum limit of 5 MB',
				duration: 10000,
			});
			return;
		}
		setIsResumeUploadedLoading(true);
		let imageUrl = '';
		if (resume && user) {
			// fileName, fileLocation, mime
			const policy = await getPolicyForS3NonVideo(resume.name, 'resumeUrls', resume.type, true);
			const uploadedUrl = await handleS3Upload(policy, resume);
			if (uploadedUrl) imageUrl = uploadedUrl;
			const { error } = await checkResumeContent(imageUrl, String(user._id));
			if (error) {
				toast({
					title: 'Resume is not valid',
					type: 'error',
				});
				setIsResumeUploadedLoading(false);
				return;
			}
			if (imageUrl) {
				handleUploadResumeLinkToDatabase(imageUrl);
			}
		} else {
			setIsResumeUploadedLoading(false);
		}
	};

	const handleUploadResumeLinkToDatabase = (resumeLink: string) => {
		updateProfileByIdFn.mutate({
			id: String(user?._id),
			data: {
				profile: {
					resumeLink,
				},
			},
		});
	};

	const handleRemoveResume = () => {
		if (isResumeRemoveLoading) {
			return;
		}
		setIsResumeRemoveLoading(true);
		updateProfileByIdFn.mutate({
			id: String(user?._id),
			data: {
				profile: {
					resumeLink: 'empty',
				},
			},
		});
	};

	return (
		<>
			<div className="flex items-center gap-2">
				<div
					className={classNames(
						'relative flex w-max items-center gap-2 rounded-[99px] bg-[#18222D] px-[10px] py-[4px] text-sm font-normal text-white',
						isResumeRemoveLoading ? 'opacity-[0.7]' : '',
					)}
					onClick={() => {
						window.open(resumeLink, '_blank');
					}}
				>
					{getFileNameFromeResumeLink(resumeLink).length > 18 ? (
						<Tooltip content={getFileNameFromeResumeLink(resumeLink)}>
							<div className="max-w-[140px] cursor-default truncate">{getFileNameFromeResumeLink(resumeLink)}</div>
						</Tooltip>
					) : (
						<div className="cursor-default">{getFileNameFromeResumeLink(resumeLink)}</div>
					)}
					{user?.flags?.isMasaiStudent ? null : (
						<RxCross2 onClick={handleRemoveResume} className="h-[12px] w-[12px] cursor-pointer text-[#CCDDFF]" />
					)}
					{isResumeRemoveLoading ? (
						<div className="absolute inset-0 m-auto w-full h-full flex items-center justify-center">
							<Spinner size={'sm'} />
						</div>
					) : null}
				</div>

				{user?.flags?.isMasaiStudent ? null : (
					<div
						onClick={() => {
							if (isResumeUploadedLoading) {
								return;
							}
							const fileInput = document.getElementById('hukumu-upload-new-resume');
							fileInput?.click();
						}}
						className="cursor-pointer text-xs font-normal text-gray-700 dark:text-[#CCDDFF] underline"
					>
						{isResumeUploadedLoading ? <Spinner /> : 'Upload new resume'}
					</div>
				)}
			</div>

			<div className="relative mt-[8px] hidden">
				<FileInput
					disabled={isResumeUploadedLoading}
					id="hukumu-upload-new-resume"
					accept=".pdf,doc,docx"
					helperText="DOC, DOCX, PDF — supported formats"
					className="w-full text-xs font-normal italic"
					onChange={event => {
						if (event.target.files) {
							handleUploadResume(event.target.files[0]);
						}
					}}
				/>
				{isResumeUploadedLoading ? <Spinner className="absolute left-[158px] top-2" /> : null}
			</div>
		</>
	);
};

export default ProfileViewResume;
